import { maskGstin } from '@oneapp/gst';
import {
  type ChatLanguage,
  type ChatMessage,
  type Choice,
  type ConversationSummary,
  MeResponseSchema,
  MetaResponseSchema,
  OPEN_REF,
  type UserFilesContent,
  type WidgetContent,
} from '@oneapp/shared';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router';
import { useAuth, useSignedIn } from '../auth/AuthProvider';
import { businessApi, businessKeys } from '../business/api';
import { ConnectionBadge } from '../business/parts';
import { chatApi } from '../chat/api';
import { Composer } from '../chat/Composer';
import { HELP_QUESTIONS } from '../chat/helpQuestions';
import { MessageList, Typing } from '../chat/MessageList';
import { StatusLine } from '../chat/StatusLine';
import { composerHint, proposalForFile } from '../chat/state';
import { useConversation } from '../chat/useConversation';
import { type WidgetData, WidgetSheet } from '../chat/WidgetSheet';
import { Drawer } from '../components/Drawer';
import { SettingsRow } from '../components/forms';
import { Icon, Mark } from '../components/icons';
import { Button, Callout } from '../components/ui';
import { ApiError, api } from '../lib/api';
import { notificationKeys, notificationsApi } from '../notifications/api';
import { approvalKeys, approvalsApi } from '../people/api';

const initials = (name: string | null) =>
  (name ?? '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0] ?? '')
    .join('')
    .toUpperCase();

/** Within this distance of the bottom, new messages scroll into view by themselves. */
const NEAR_BOTTOM_PX = 120;
/** Only say "Reconnecting…" if it takes longer than a blip. */
const RECONNECT_NOTICE_MS = 3_000;

/**
 * The chat: the latest conversation (or the one in the address), live. Bot messages come from the
 * server's conversation engine; taps and typed messages show at once and are confirmed by the server.
 */
export function Chat() {
  const { t, i18n } = useTranslation();
  const params = useParams();
  const [search, setSearch] = useSearchParams();
  const location = useLocation();
  const conversationId =
    params.conversationId ?? (location.state as { conversationId?: string } | null)?.conversationId;
  const chat = useConversation(conversationId);
  const { state } = chat;
  const [menuOpen, setMenuOpen] = useState(false);
  const [widgetFor, setWidgetFor] = useState<string | null>(null);
  const [now, setNow] = useState(() => new Date());

  // Keep "Today" right when the chat stays open past midnight.
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(timer);
  }, []);

  const handlers = {
    choose: (message: ChatMessage, choice: Choice) =>
      chat.send(
        { type: 'choice', messageId: message.id, choiceId: choice.id },
        { type: 'user_choice', choiceId: choice.id, label: choice.label },
      ),
    openWidget: (message: ChatMessage) => setWidgetFor(message.id),
    retry: chat.retry,
    remove: chat.discard,
  };

  const widgetMessage = widgetFor ? state.messages.find((m) => m.id === widgetFor) : undefined;
  const widget = widgetMessage?.content.type === 'widget' ? (widgetMessage.content as WidgetContent).widget : null;

  // A widget that asks to open by itself does so when it arrives live (not when reloading the chat): the
  // newest one in the bot's latest turn, even with options after it (a code page, then "Send a new code").
  const autoOpened = useRef(new Set<string>());
  useEffect(() => {
    let candidate: ChatMessage | undefined;
    for (let i = state.messages.length - 1; i >= 0; i--) {
      const message = state.messages[i] as ChatMessage;
      if (message.from !== 'bot') break;
      if (message.content.type === 'widget') {
        candidate = message;
        break;
      }
    }
    if (candidate?.status !== 'active') return;
    if (state.initialIds.has(candidate.id) || autoOpened.current.has(candidate.id)) return;
    if (!(candidate.content as WidgetContent).widget.autoOpen) return;
    autoOpened.current.add(candidate.id);
    setWidgetFor(candidate.id);
  }, [state.messages, state.initialIds]);

  useEffect(() => {
    if (widgetMessage && widgetMessage.status !== 'active' && widget?.kind !== 'page') {
      setWidgetFor((current) => (current === widgetMessage.id ? null : current));
    }
  }, [widgetMessage, widget]);

  const submitWidget = (data: WidgetData, echo?: UserFilesContent) => {
    if (!widgetMessage || !widget) return;
    const hidden =
      widget.kind === 'otp' ||
      (widget.kind === 'form' && widget.fields.some((f) => f.sensitive || f.type === 'password'));
    if (hidden) {
      if (!('challengeId' in widget) || !widget.challengeId) return;
      const field =
        widget.kind === 'form' ? widget.fields.find((f) => f.sensitive || f.type === 'password')?.name : 'otp';
      chat.sendCode(
        widget.challengeId,
        String(data[field ?? 'password'] ?? ''),
        widget.kind === 'form' ? t('chat.passwordSaid') : (widget.submitSummary ?? '••••••'),
        'page',
      );
      return;
    }
    chat.send(
      { type: 'widget_submit', messageId: widgetMessage.id, widgetId: widget.id, data },
      echo ??
        (hidden
          ? { type: 'user_submit', summary: '••••••', icon: 'lock' }
          : {
              type: 'user_submit',
              summary: (widget.kind === 'payment' && widget.submitSummary) || widget.title,
              icon: 'check-circle',
            }),
    );
    setWidgetFor(null);
  };
  const widgetAction = (action: string) => {
    if (!widgetMessage || !widget) return;
    // Coming back from one of the app's pages closes it; the chat carries on from what happened there.
    if (widget.kind === 'page') setWidgetFor(null);
    const summary =
      widget.kind === 'page'
        ? action === 'review'
          ? t(widget.id.startsWith('gstr1a-') ? 'chat.widget.pageReviewFixes' : 'chat.widget.pageReview')
          : action === 'saved'
            ? t('chat.widget.pageSaved', { title: widget.title })
            : t('chat.widget.pageBack', { title: widget.title })
        : t('chat.widget.resend');
    // A page opened from an older message: the chat has moved on, so there's nothing to tell it.
    if (widget.kind === 'page' && widgetMessage.status !== 'active') return;
    chat.send(
      { type: 'widget_action', messageId: widgetMessage.id, widgetId: widget.id, action },
      { type: 'user_submit', summary },
    );
  };

  // A notice's link (/?open=gstr3b:<business>:<period>, or note:<id>): opens it in the chat, once, as its chip would.
  const openRef = search.get('open');
  const opened = useRef<string | null>(null);
  useEffect(() => {
    if (!openRef || state.status !== 'ready' || opened.current === openRef) return;
    opened.current = openRef;
    setSearch(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete('open');
        return next;
      },
      { replace: true, state: location.state },
    );
    if (!OPEN_REF.test(openRef)) return;
    const [form, , period = ''] = openRef.split(':');
    const month = t(`filings.months.${Number(period.slice(5, 7))}`);
    const chip = form === 'gstr1' ? 'chat.openGstr1' : form === 'gstr1a' ? 'chat.openGstr1a' : 'chat.openGstr3b';
    chat.send(
      { type: 'open', ref: openRef },
      {
        type: 'user_choice',
        choiceId: openRef,
        // Gap 4b: a credit note a buyer rejected reads as the notice's title.
        label:
          form === 'note' ? t('chat.openNote') : t(chip, { month: i18n.language === 'en' ? month.slice(0, 3) : month }),
      },
    );
  }, [openRef, state.status, chat, setSearch, location.state, t, i18n.language]);

  const sendText = (text: string) => {
    const proposalId = proposalForFile(state.messages, text);
    return chat.send({ type: 'text', text, ...(proposalId ? { proposalId } : {}) }, { type: 'user_text', text });
  };

  // Help's common questions (/?ask=due_dates): asked once, as if typed, so the chat answers them.
  const askRef = search.get('ask');
  const asked = useRef<string | null>(null);
  useEffect(() => {
    if (!askRef || state.status !== 'ready' || asked.current === askRef) return;
    asked.current = askRef;
    setSearch(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete('ask');
        return next;
      },
      { replace: true, state: location.state },
    );
    if (!(HELP_QUESTIONS as readonly string[]).includes(askRef)) return;
    const text = t(`help.questions.${askRef}`);
    chat.send({ type: 'text', text }, { type: 'user_text', text });
  }, [askRef, state.status, chat, setSearch, location.state, t]);
  const sendSecret = (value: string) => {
    const hint = composerHint(state);
    if (!hint?.challengeId) return;
    chat.sendCode(hint.challengeId, value, hint.secretKind === 'password' ? t('chat.passwordSaid') : '••••••', 'box');
  };

  const scroller = useChatScroll(state.messages, state.outgoing.length, state.typing, state.status === 'ready');

  return (
    <div className="frame">
      <header className="appbar">
        <button type="button" className="iconbtn" aria-label={t('app.menu')} onClick={() => setMenuOpen(true)}>
          <Icon name="menu" size={22} />
        </button>
        <Mark size={34} />
        <div className="appbar__title">
          <h1 className="appbar__name">{t('app.name')}</h1>
          <span className="appbar__sub">{t('app.tagline')}</span>
        </div>
        <Bell />
      </header>

      {state.conversation?.business ? <BusinessBar business={state.conversation.business} /> : null}
      <ConnectionBanner connection={state.status === 'ready' && !state.ended ? state.connection : 'open'} />
      <StatusLine />

      <div className="messages-wrap">
        <main
          className="messages"
          aria-label={t('chat.label')}
          // biome-ignore lint/a11y/noNoninteractiveTabindex: the chat scrolls; the keyboard must reach it even when no message has a button
          tabIndex={0}
          ref={scroller.ref}
          onScroll={scroller.onScroll}
        >
          {state.status === 'ready' ? (
            <div className="messages__inner messages__inner--top">
              {state.hasMore ? (
                <Button
                  variant="ghost"
                  small
                  inline
                  className="earlier"
                  busy={state.loadingOlder}
                  busyLabel={t('chat.loadingEarlier')}
                  onClick={() => {
                    scroller.keepPosition();
                    void chat.loadOlder();
                  }}
                >
                  {t('chat.earlier')}
                </Button>
              ) : null}
              <div className="messages__log" role="log" aria-live="polite" aria-relevant="additions">
                <MessageList state={state} handlers={handlers} now={now} />
              </div>
              {state.typing || state.outgoing.some((o) => o.state === 'sending') ? (
                <Typing progress={state.progress} />
              ) : null}
            </div>
          ) : (
            <ChatStatus state={state.status} reason={state.loadError} onRetry={chat.reload} />
          )}
        </main>
        {scroller.showJump ? (
          <button type="button" className="jump" onClick={scroller.jump}>
            <Icon name="arrow-down" size={16} />
            {t('chat.newMessages')}
          </button>
        ) : null}
      </div>

      {state.notice ? (
        <p className="chat-notice" role="status">
          <Icon name="info" size={16} />
          {t(`chat.${state.notice}`)}
        </p>
      ) : null}

      {state.ended === 'deleted' ? (
        <EndedBar />
      ) : (
        <Composer
          hint={composerHint(state)}
          codeError={
            chat.invalidCode === composerHint(state)?.challengeId ? composerHint(state)?.codeFormatError : undefined
          }
          disabled={state.status !== 'ready'}
          onSend={sendText}
          onSecret={sendSecret}
        />
      )}

      <Menu open={menuOpen} onClose={() => setMenuOpen(false)} conversation={state.conversation} />
      <WidgetSheet
        key={widgetFor ?? 'none'}
        widget={widget}
        codeError={
          widget && 'challengeId' in widget && chat.invalidCode === widget.challengeId
            ? widget.codeFormatError
            : undefined
        }
        onClose={() => setWidgetFor(null)}
        onSubmit={submitWidget}
        onAction={widgetAction}
      />
    </div>
  );
}

/**
 * Follows the conversation to the bottom as messages arrive — unless the person has scrolled up to
 * read, in which case a "New messages" button appears instead. Loading earlier messages keeps the
 * place.
 */
function useChatScroll(messages: ChatMessage[], outgoing: number, typing: boolean, ready: boolean) {
  const ref = useRef<HTMLElement>(null);
  const nearBottom = useRef(true);
  const fromBottom = useRef<number | null>(null);
  const seen = useRef<{ count: number; last: string | undefined }>({ count: 0, last: undefined });
  const [showJump, setShowJump] = useState(false);

  const toBottom = (smooth: boolean) => {
    const el = ref.current;
    if (!el) return;
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    el.scrollTo?.({ top: el.scrollHeight, behavior: smooth && !reduce ? 'smooth' : 'auto' });
    if (!el.scrollTo) el.scrollTop = el.scrollHeight;
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: follow the bottom whenever what's shown changes
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const last = messages[messages.length - 1]?.id;
    const before = seen.current;
    seen.current = { count: messages.length, last };
    if (fromBottom.current !== null) {
      // Earlier messages were added above: stay on the same message.
      el.scrollTop = el.scrollHeight - fromBottom.current;
      fromBottom.current = null;
      return;
    }
    if (before.count === 0 || nearBottom.current) {
      toBottom(false);
      setShowJump(false);
    } else if (last !== before.last) {
      setShowJump(true);
    }
  }, [messages, outgoing, typing]);

  // Content can also grow without a new message (fonts arriving, a card opening): stay at the bottom.
  useEffect(() => {
    const el = ref.current;
    const inner = el?.firstElementChild;
    if (!ready || !el || !inner || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => {
      if (nearBottom.current && fromBottom.current === null) el.scrollTop = el.scrollHeight;
    });
    observer.observe(inner);
    return () => observer.disconnect();
  }, [ready]);

  return {
    ref,
    showJump,
    onScroll: () => {
      const el = ref.current;
      if (!el) return;
      nearBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_PX;
      if (nearBottom.current) setShowJump(false);
    },
    jump: () => {
      toBottom(true);
      setShowJump(false);
    },
    keepPosition: () => {
      const el = ref.current;
      if (el) fromBottom.current = el.scrollHeight - el.scrollTop;
    },
  };
}

/** The bell (M1): the notices, with how many are unread; checked every minute and when the app is back in view. */
function Bell() {
  const { t } = useTranslation();
  const unread = useQuery({
    queryKey: notificationKeys.unread,
    queryFn: ({ signal }) => notificationsApi.unread(signal),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  });
  const count = unread.data?.unread ?? 0;
  return (
    <Link
      className="iconbtn bell"
      to="/notifications"
      aria-label={count ? t('app.notificationsUnread', { count }) : t('app.notifications')}
    >
      <Icon name="bell" size={21} />
      {count ? (
        <span className="bell__count" aria-hidden="true">
          {count > 99 ? '99+' : count}
        </span>
      ) : null}
    </Link>
  );
}

/** The business this chat is about (E4): tap for its page. */
function BusinessBar({ business }: { business: NonNullable<ConversationSummary['business']> }) {
  const { t } = useTranslation();
  return (
    <Link
      className="bizbar"
      to={`/businesses/${business.id}`}
      aria-label={t('businesses.bar', { name: business.name, gstin: maskGstin(business.gstin) })}
    >
      <span className="icon-tile icon-tile--small">
        <Icon name="building" size={18} />
      </span>
      <span className="bizbar__text">
        <span className="bizbar__name">{business.name}</span>
        <span className="bizbar__gstin">{maskGstin(business.gstin)}</span>
      </span>
      <ConnectionBadge status={business.connection} />
    </Link>
  );
}

function ConnectionBanner({ connection }: { connection: string }) {
  const { t } = useTranslation();
  const [slow, setSlow] = useState(false);
  const reconnecting = connection === 'reconnecting' || connection === 'connecting';
  useEffect(() => {
    if (!reconnecting) {
      setSlow(false);
      return;
    }
    const timer = setTimeout(() => setSlow(true), RECONNECT_NOTICE_MS);
    return () => clearTimeout(timer);
  }, [reconnecting]);
  const message = connection === 'offline' ? t('chat.offline') : reconnecting && slow ? t('chat.reconnecting') : null;
  // The live region stays in the page (empty when all is well) so changes are announced.
  return (
    <div className="conn" role="status">
      {message ? (
        <>
          <Icon name={connection === 'offline' ? 'wifi-off' : 'refresh'} size={16} />
          {message}
        </>
      ) : null}
    </div>
  );
}

function ChatStatus({
  state,
  reason,
  onRetry,
}: {
  state: 'loading' | 'error' | 'notFound' | 'ready';
  reason: 'offline' | 'server' | undefined;
  onRetry: () => void;
}) {
  const { t } = useTranslation();
  if (state === 'notFound') {
    return (
      <div className="chat-status">
        <p>{t('chat.notFound')}</p>
        <Link className="btn btn--secondary btn--inline" to="/">
          {t('chat.goToChat')}
        </Link>
      </div>
    );
  }
  if (state === 'error') {
    return (
      <div className="chat-status">
        <Callout tone="error" icon="alert-triangle" role="alert">
          {t('chat.loadFailed')} {t(reason === 'server' ? 'errors.server' : 'errors.network')}
        </Callout>
        <Button inline icon="refresh" onClick={onRetry}>
          {t('app.retry')}
        </Button>
      </div>
    );
  }
  return (
    <div className="chat-status" role="status">
      <span className="spinner spinner--dark" aria-hidden="true" />
      <p>{t('chat.loading')}</p>
    </div>
  );
}

function EndedBar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  return (
    <div className="composer-area composer-area--ended">
      <p className="chat-ended">{t('chat.deleted')}</p>
      <Button
        inline
        small
        icon="plus"
        busy={busy}
        onClick={async () => {
          setBusy(true);
          try {
            const { conversation } = await chatApi.start(true);
            navigate('/', { state: { conversationId: conversation.id } });
          } catch {
            setBusy(false);
          }
        }}
      >
        {t('chat.startNew')}
      </Button>
    </div>
  );
}

/** P1 · Menu: who's signed in, the chats, businesses, filings, language, privacy, sign out. More rows arrive with their phases. */
function Menu({
  open,
  onClose,
  conversation,
}: {
  open: boolean;
  onClose: () => void;
  conversation: ConversationSummary | null;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const auth = useAuth();
  const { me, roles } = useSignedIn();
  const [signingOut, setSigningOut] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState<'offline' | 'server' | null>(null);
  const meta = useQuery({
    queryKey: ['meta'],
    // Signed in: flags turned on for this person come back on.
    queryFn: ({ signal }) => api.get('/api/v1/meta', MetaResponseSchema, { auth: true, signal }),
    staleTime: 5 * 60_000,
    enabled: open,
  });
  const businesses = useQuery({
    queryKey: businessKeys.all,
    queryFn: ({ signal }) => businessApi.list(signal),
    enabled: open,
  });
  // Approvals (Phase 14): the row shows when there are any, with how many wait for this person.
  const approvals = useQuery({
    queryKey: approvalKeys.list(null),
    queryFn: ({ signal }) => approvalsApi.list(null, signal),
    enabled: open,
  });
  // A chat is named once it reaches a service, so look the name up afresh each time the menu opens.
  const recent = useQuery({
    queryKey: ['conversations', 'recent'],
    queryFn: () => chatApi.list(),
    enabled: open && conversation !== null,
    staleTime: 0,
  });
  const title = recent.data?.conversations.find((c) => c.id === conversation?.id)?.title ?? conversation?.title ?? null;

  const go = (path: string) => {
    onClose();
    navigate(path);
  };

  const newChat = async () => {
    setStarting(true);
    setStartError(null);
    try {
      const { conversation: fresh } = await chatApi.start(true);
      onClose();
      navigate('/', { state: { conversationId: fresh.id } });
    } catch (error) {
      setStartError(error instanceof ApiError && error.status !== 0 ? 'server' : 'offline');
    } finally {
      setStarting(false);
    }
  };

  const switchLanguage = async () => {
    const next: ChatLanguage = me.chatLanguage === 'hi' ? 'en' : 'hi';
    setSwitching(true);
    try {
      const result = await api.patch('/api/v1/me', MeResponseSchema, { auth: true, body: { chatLanguage: next } });
      auth.update(result.user, result.needs);
    } catch {
      // Stays as it was; the row shows the language actually saved.
    } finally {
      setSwitching(false);
    }
  };

  return (
    <Drawer open={open} onClose={onClose} label={t('menu.title')}>
      <div className="drawer__head">
        <span className="avatar" aria-hidden="true">
          {initials(me.name) || <Icon name="smartphone" size={20} />}
        </span>
        <span className="drawer__who">
          <span className="drawer__name">{me.name}</span>
          <span className="drawer__phone">{me.phoneMasked}</span>
        </span>
        <button type="button" className="iconbtn" aria-label={t('menu.editProfile')} onClick={() => go('/profile')}>
          <Icon name="pencil" size={18} />
        </button>
      </div>
      <p className="section-label">{t('menu.chats')}</p>
      <div className="settings">
        <SettingsRow
          icon="message-circle"
          label={t('menu.currentChat')}
          {...(title ? { detail: title } : {})}
          onClick={onClose}
        />
        <SettingsRow
          icon="plus"
          label={starting ? t('menu.startingChat') : t('menu.newChat')}
          busy={starting}
          onClick={() => void newChat()}
        />
        <SettingsRow icon="list" label={t('menu.allChats')} onClick={() => go('/chats')} />
      </div>
      {startError ? (
        <Callout tone="error" icon="alert-triangle" role="alert">
          {t(startError === 'server' ? 'errors.server' : 'errors.network')}
        </Callout>
      ) : null}
      <p className="section-label">{t('menu.account')}</p>
      <div className="settings">
        <SettingsRow
          icon="building"
          label={t('menu.businesses')}
          {...(businesses.data?.businesses.length ? { value: String(businesses.data.businesses.length) } : {})}
          onClick={() => go('/businesses')}
        />
        {approvals.data?.requests.length ? (
          <SettingsRow
            icon="check-circle"
            label={t('menu.approvals')}
            {...(approvals.data.waiting ? { value: String(approvals.data.waiting) } : {})}
            onClick={() => go('/approvals')}
          />
        ) : null}
        <SettingsRow icon="file-check" label={t('menu.filings')} onClick={() => go('/filings')} />
        <SettingsRow icon="bell" label={t('menu.reminders')} onClick={() => go('/reminders')} />
        <SettingsRow
          icon="globe"
          label={t('menu.language')}
          value={t(`languages.${me.chatLanguage}`)}
          busy={switching}
          onClick={() => void switchLanguage()}
        />
        <SettingsRow icon="shield-check" label={t('menu.privacy')} onClick={() => go('/privacy-and-consents')} />
        <SettingsRow icon="help-circle" label={t('menu.help')} onClick={() => go('/help')} />
        {roles.length ? (
          <SettingsRow
            icon="briefcase"
            label={t('menu.admin')}
            detail={roles.map((role) => t(`admin.roles.${role}`)).join(', ')}
            onClick={() => go('/admin')}
          />
        ) : meta.data && meta.data.env !== 'production' ? (
          <SettingsRow icon="key" label={t('menu.keys')} onClick={() => go('/admin/keys')} />
        ) : null}
      </div>
      <div className="settings">
        <SettingsRow
          icon="log-out"
          label={signingOut ? t('menu.signingOut') : t('menu.signOut')}
          danger
          busy={signingOut}
          onClick={() => {
            setSigningOut(true);
            void auth.signOut();
          }}
        />
      </div>
    </Drawer>
  );
}
